import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

export const writersDashboardCreateCommand: SelfContainedCommand = {
  id: 'writers_dashboard_create',
  description: 'Create a new Writing Hub for managing your self-publishing workflow',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the dashboard (default: "Writing Hub")',
        required: false,
        defaultValue: 'Writing Hub'
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'The ID of the space to create the dashboard in',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'My Book Dashboard' },
        expectedOutput: createSuccessResult('Writing Hub created successfully: "My Book Dashboard"', {}),
        description: 'Create a dashboard with a custom title'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 500,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { title = 'Writing Hub', spaceId: providedSpaceId } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      const now = new Date().toISOString();
      const newDashboardData = {
        entityType: 'WritersDashboard',
        name: title.trim(),
        spaceId: spaceId,
        edge_pos: '!',
        skeleton: {
          '@type': 'WritersDashboard',
          version: 'v2',
          architecture: 'unified',
          createdAt: now,
          updatedAt: now
        },
        created_at: now
      };
      
      const entityStoreModule = await import('../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();
      
      const newDashboard = await entityStore.actions.createEntity(newDashboardData);
      
      const dashboardId = newDashboard._id || newDashboard.id;
      const dashboardLlmId = newDashboard.llmId;
      
      if (dashboardLlmId && dashboardId) {
        const { llmIdMapper } = await import('../../../utils/llmIdMapper');
        llmIdMapper.registerMapping(dashboardLlmId, dashboardId, 'document', 'WritersDashboard', newDashboard.name);
      }
      
      // Add to dataspace tree
      if (dashboardId && spaceId) {
        try {
          const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          const edgeKey = `${spaceId}:${dashboardId}`;
          
          treeStore._applyAddNode(edgeKey, {
            id: dashboardId,
            parentId: spaceId,
            pos: "!", // Exclamation mark comes before all alphanumeric in ASCII - guaranteed first position
            expanded: false,
            kind: 'canonical'
          });
        } catch (treeError) {
          console.warn('[writers_dashboard_create] Failed to add to tree (non-fatal):', treeError);
        }
      }
      
      // Open window
      let windowInfo = null;
      try {
        const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
        const { executeCommand } = useCommandRegistryStore.getState();
        
        const result = (await executeCommand('space_open_window', {
          entityId: dashboardId
        })) as StandardToolResult;
        
        if (result.status === 'success') {
          windowInfo = {
            id: result.data?.windowId,
            title: title.trim(),
            appType: 'writers_dashboard',
            entityId: dashboardId,
            entityLlmId: dashboardLlmId
          };
        }
      } catch (windowError) {
        console.error('[writers_dashboard_create] Failed to create window:', windowError);
      }
      
      return createSuccessResult(
        `Writing Hub created successfully: "${title.trim()}"`,
        {
          entityId: dashboardId,
          dashboard: {
            id: dashboardId,
            llmId: dashboardLlmId,
            title: title.trim(),
            entityType: 'WritersDashboard',
            created_at: newDashboard.created_at
          },
          window: windowInfo
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            affectedEntities: [dashboardId, ...(windowInfo ? [windowInfo.id] : [])]
          } as any,
          ui: {
            refreshContext: true,
            autoFocus: windowInfo?.id,
            resultPreview: 'summary'
          }
        }
      );
    } catch (error: any) {
      return createErrorResult(
        'Dashboard creation failed',
        `Failed to create Writing Hub: ${error.message || 'Unknown error'}`,
        'execution',
        { 
          data: { title, spaceId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// Register in entity creation registry
entityCreationRegistry.register({
  commandId: 'writers_dashboard_create',
  entityType: 'WritersDashboard',
  displayName: 'Writing Hub',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'BookOpen',
    phosphorWeight: 'duotone'
  },
  description: 'Organize your self-publishing workflow',
  canCreateInFolder: false,
  openAfterCreate: true,
  category: 'productivity',
  sortOrder: 1
});
